import type { ReactNode } from "react";
import { CircleAlert, CircleCheck, TriangleAlert } from "lucide-react";
import { cx } from "@amber/ui";

type Tone = "ok" | "attention" | "failed";

export const TONE_TEXT: Record<Tone, string> = {
  ok: "text-muted",
  attention: "font-strong text-waiting",
  failed: "font-strong text-attention",
};

export function ToneIcon({ tone }: { tone: Tone }) {
  if (tone === "failed")
    return <CircleAlert size={13} strokeWidth={1.8} aria-hidden="true" className="mt-px flex-none text-attention" />;
  if (tone === "attention")
    return <TriangleAlert size={13} strokeWidth={1.8} aria-hidden="true" className="mt-px flex-none text-waiting" />;
  return <CircleCheck size={13} strokeWidth={1.8} aria-hidden="true" className="mt-px flex-none text-muted" />;
}

export function Table({
  label,
  head,
  minWidth,
  children,
}: {
  label: string;
  head: ReactNode;
  minWidth?: number;
  children: ReactNode;
}) {
  return (
    <div className="overflow-auto rounded-lg border border-divider">
      <table
        aria-label={label}
        className="w-full border-separate border-spacing-0 text-ui"
        style={minWidth ? { minWidth } : undefined}
      >
        <thead>
          <tr>{head}</tr>
        </thead>
        <tbody className="[&>tr:last-child>td]:border-b-0">{children}</tbody>
      </table>
    </div>
  );
}

export function Th({
  children,
  right = false,
  className,
}: {
  children: ReactNode;
  right?: boolean;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cx(
        "border-b border-border bg-surface-2 px-2.5 py-[7px] text-sm font-medium whitespace-nowrap text-muted",
        right ? "text-right" : "text-left",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  right = false,
  className,
  colSpan,
}: {
  children: ReactNode;
  right?: boolean;
  className?: string;
  colSpan?: number;
}) {
  return (
    <td
      colSpan={colSpan}
      className={cx(
        "border-b border-divider px-2.5 py-[9px] align-middle text-muted",
        right && "text-right whitespace-nowrap",
        className,
      )}
    >
      {children}
    </td>
  );
}

export function Name({ children, detail }: { children: ReactNode; detail?: string | null }) {
  return (
    <>
      <span className="font-strong text-fg">{children}</span>
      {detail ? <span className="block text-sm text-muted">{detail}</span> : null}
    </>
  );
}
